import { ClaimType } from '../types/claim.types';
import type { ClaimData } from '../types/claim.types';
import { defaultTrackingBaseUrl } from '../web/pulse/constants/claims';
import { ClaimState } from '../types';
import { claimStateMapper } from './claims-orders.helper';

/*
  same functionality in ~/apps/valet/helpers/claims.helper.ts

  TODO: merge in the future
*/

/*
  Claim is considered in the item level if:
  * claimType is `MULTIPLE`
  * claim has multiple values for `claimItem.claimType`
*/

export function isClaimItemLevel(claim: ClaimData) {
  if (String(claim.claimType).toUpperCase() === ClaimType.MULTIPLE) {
    return true;
  }

  const uniqueClaimTypes = new Set(claim?.claimItems?.map(item => item.claimType));
  return uniqueClaimTypes.size > 1;
}

export function isClaimOrderLevel(claim: ClaimData) {
  return !isClaimItemLevel(claim);
}

export const buildTrackingLink = ({
  trackingUrl,
  trackingNumber,
}: {
  trackingUrl?: string;
  trackingNumber?: string;
}) => {
  return trackingUrl ? trackingUrl : defaultTrackingBaseUrl + `/${trackingNumber}`;
};

export const formatClaimType = (claimType?: string) => {
  return claimType?.replace(/_/g, ' ').toLowerCase() ?? '';
};

export const formatClaimState = (claimState: ClaimState) => {
  const state = formatClaimType(claimState);
  return claimStateMapper.label(state);
};

export const ClaimSessionStatusToText: { [key: string]: string } = {
  ISSUES: '/issues',
  PROCESS: '/process',
  ISSUES_ITEMS: '/issues/items',
  ISSUES_SHIPMENT: '/issues/shipment',
  ISSUES_SHIPMENT_DETAILS: '/issues/shipment/details',
  RESOLUTION: '/resolution',
  RESOLUTION_REVIEW: '/resolution/review',
  MULTIPLE_SHIPMENTS: '/multiple-shipments',
};

/**
 * Calculate total claimed quantity for a specific fulfillment item
 *
 * @param claims Array of claims
 * @param sourceItemId Source item ID to match against
 * @returns Total claimed quantity for the item
 */
export function calculateTotalClaimedQuantity(
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
  sourceItemId: string,
): number {
  const claimItems = claims
    .flatMap(claim => claim.claimItems)
    .filter(claimItem => claimItem.sourceItemId === sourceItemId);

  return claimItems.reduce((total: number, claimItem) => total + claimItem.quantity, 0);
}

/**
 * Check if a fulfillment item can have more claims filed based on its current claimed quantity
 *
 * @param claims Array of claims
 * @param sourceItemId Source item ID to match against
 * @param fulfillmentQuantity Total quantity in the fulfillment
 * @returns Whether more claims can be filed for this item
 */
export function checkFulfillmentItemCanFileClaim(
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
  sourceItemId: string,
  fulfillmentQuantity: number,
): boolean {
  const claimItems = claims
    .flatMap(claim => claim.claimItems)
    .filter(claimItem => claimItem.sourceItemId === sourceItemId);

  const totalClaimedQuantity = claimItems.reduce((total: number, claimItem) => total + claimItem.quantity, 0);

  return totalClaimedQuantity < fulfillmentQuantity;
}

/**
 * Check fulfillment can at least file claim on one item
 *
 * @param fulfillment
 * @param claims
 * @returns
 */
export function checkFulfillmentCanFileClaim(
  fulfillment: {
    items: Array<{
      item: {
        sourceItemId: string;
      };
      quantity: number;
    }>;
  },
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
): boolean {
  const canAtLeastClaimOnOneFulfillmentItem = fulfillment.items.some(fulfillmentItem =>
    checkFulfillmentItemCanFileClaim(claims, fulfillmentItem.item.sourceItemId, fulfillmentItem.quantity),
  );

  return canAtLeastClaimOnOneFulfillmentItem;
}
